<script>
import { createNamespacedHelpers } from "vuex"
import { RCA_CHECKOUT_ENABLED } from "@/store/modules/subscription.js"

const { mapState, mapActions } = createNamespacedHelpers("pricing")

export default {
  name: "CPricing",
  props: {
    activePlanId: {
      type: String,
      default: "",
    },
    periodUnit: {
      type: String,
      default: "month",
    },
    activeSubscription: {
      type: Object,
      default: null,
    },
    useCatalog: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["fetched"],
  data() {
    return {
      isFetching: this.$store.state.pricing.pricingPlans.length === 0,
    }
  },
  computed: {
    ...mapState(["pricingPlans"]),
    normalizedPlans() {
      const options = {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
        style: "currency",
      }
      const priceFormats = {
        EUR: new Intl.NumberFormat(undefined, {
          ...options,
          currency: "EUR",
        }),
        USD: new Intl.NumberFormat(undefined, {
          ...options,
          currency: "USD",
        }),
      }
      return this.pricingPlans
        .filter((plan) => {
          const { id, bundle, source } = plan
          if (source === "salesforce") {
            return true
          }
          if (id === "prod-28-08-2017_prtg-trial") {
            return true
          }
          if (!bundle) return false

          const [head, ...tail] = bundle.split("-")
          if (head !== "prod") {
            return false
          }
          return new Date(`${tail[2]}-${tail[1]}-${tail[0]}`) > new Date("2022-01-01")
        })
        .map(plan => ({
          ...plan,
          priceInCents: plan.price,
          price: priceFormats[plan.currencyCode]?.format(plan.price / 100) || plan.price,
          active: plan.id === this.activePlanId,
        }))
    },
    trialPlan() {
      // While the switch is off, trials start in Chargebee, which only knows its own trial plan.
      if (!RCA_CHECKOUT_ENABLED) {
        return this.normalizedPlans.find(
          ({ trial, source, currencyCode }) => trial && source !== "salesforce" && currencyCode === this.visitor.currencyCode,
        ) || {}
      }
      return this.normalizedPlans.find(
        ({ id }) => id === "hosted_trial",
      ) || this.normalizedPlans.find(
        ({ trial, currencyCode }) => trial && currencyCode === this.visitor.currencyCode,
      ) || {}
    },
    baseMonthAnnualPlans() {
      return this.getFilteredMainPlans()
    },
    multiAnnualPlans() {
      return this.getFilteredMultiAnnualPlans()
    },
    visitor() {
      return this.$store.state.visitor.visitor
    },
    currentPricingPlan() {
      const basePlan = this.normalizedPlans.find(({ id }) => id === this.activePlanId) || this.planFromSubscription()

      if (!basePlan.id || !this.activeSubscription) return basePlan
      const actualAmountInCents = this.activeSubscription.planAmount

      // Number.isFinite, not a null check: planAmount is a Chargebee field and Salesforce-backed
      // subscriptions simply do not carry one, so it arrives undefined — which passes `!== null`
      // and then formats as NaN, wiping out the perfectly good catalog price below.
      if (Number.isFinite(actualAmountInCents)) {
        const options = {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
          style: "currency",
        }
        const formatter = new Intl.NumberFormat(undefined, {
          ...options,
          currency: this.activeSubscription.currency || basePlan.currencyCode,
        })

        const nextAmountInCents = this.activeSubscription.nextPaymentAmount

        return {
          ...basePlan,
          price: formatter.format(actualAmountInCents / 100),
          originalPlanPrice: basePlan.price,
          // What the next contract year of a Salesforce subscription costs. Absent in the last year
          // and on subscriptions without a stored price schedule, which keep the renewal price.
          nextYearPrice: Number.isFinite(nextAmountInCents) ? formatter.format(nextAmountInCents / 100) : null,
        }
      }
      return basePlan
    },
    canHaveMonthlyAndAnnualPlans() {
      return this.hasPlan && this.currentPricingPlan?.periodUnit === "month"
    },
    hasPlan() {
      return Object.entries(this.currentPricingPlan).length > 0
    },
    isTrial() {
      return this.currentPricingPlan?.trial || false
    },
    is3YearMYPA() {
      return this.currentPricingPlan?.id?.includes("3_years_paid_annually") || false
    },
    is5YearMYPA() {
      return this.currentPricingPlan?.id?.includes("5_years_paid_annually") || false
    },
    is3YearRegular() {
      return this.currentPricingPlan?.period === 3 && !this.currentPricingPlan?.id?.includes("paid_annually")
    },
    is5YearRegular() {
      return this.currentPricingPlan?.period === 5 && !this.currentPricingPlan?.id?.includes("paid_annually")
    },
    isMonthly() {
      return this.currentPricingPlan?.periodUnit === "month"
    },
    is1Year() {
      return this.currentPricingPlan?.period === 1 && !this.currentPricingPlan?.id?.includes("paid_annually")
    },
  },
  async created() {
    await this.$store.dispatch("visitor/fetchVisitor")
    await this.fetchPricingPlans()
    this.isFetching = false
    this.$emit("fetched")
  },
  methods: {
    ...mapActions(["fetchPricingPlans"]),

    // The catalog is a second request and can fail or lack the plan; the subscription still knows its
    // own plan id, so the cards need not depend on the catalog. Salesforce plan ids only
    // ("hosted_500_3_years_paid_annually"), the price comes from planAmount further down.
    planFromSubscription() {
      const match = /^hosted_(\d+)_/.exec(this.activePlanId)
      if (!match || !this.activeSubscription) return {}
      return {
        id: this.activePlanId,
        name: `Hosted ${match[1]}`,
        sensors: Number(match[1]),
        period: 1,
        periodUnit: "year",
        currencyCode: this.activeSubscription.currency,
        active: true,
      }
    },

    getFilteredMainPlans() {
      // 1. Scoped logic for Checkout Wizard (New Salesforce Catalog)
      if (this.useCatalog) {
        return this.normalizedPlans
          .filter(({ trial, source }) => !trial && source === "salesforce")
          .filter(({ currencyCode }) => currencyCode === this.visitor.currencyCode)
      }

      // 2. Preserved legacy logic for everywhere else (Manage view, etc.)
      let filteredPlans = this.normalizedPlans
        .filter(({ trial }) => !trial)
        .filter(({ source }) => source !== "salesforce")
        .filter(({ currencyCode }) => {
          if (this.hasPlan) {
            return currencyCode === this.currentPricingPlan.currencyCode
          }
          return currencyCode === this.visitor.currencyCode
        })

      // New subscriptions (no current plan): Only show 3-year MYPA plans
      if (!this.hasPlan) {
        return filteredPlans.filter(plan =>
          plan.period === 1
          && plan.periodUnit === "year"
          && plan.id
          && plan.id.includes("3_years_paid_annually"),
        )
      }

      // Special handling for monthly users when monthly tab is selected
      if (this.periodUnit === "month" && this.isMonthly) {
        return filteredPlans.filter(plan => plan.periodUnit === "month")
      }

      // For all other cases (annual tab or non-monthly users), show annual plans
      if (this.isTrial) {
        // Trial users: Only show 3-year MYPA plans
        return filteredPlans.filter(plan =>
          plan.period === 1
          && plan.periodUnit === "year"
          && plan.id
          && plan.id.includes("3_years_paid_annually"),
        )
      }
      else if (this.is3YearMYPA) {
        // 3-year MYPA users: Only show other 3-year MYPA plans
        return filteredPlans.filter(plan =>
          plan.period === 1
          && plan.periodUnit === "year"
          && plan.id
          && plan.id.includes("3_years_paid_annually"),
        )
      }
      else if (this.is5YearMYPA) {
        // 5-year MYPA users: Only show other 5-year MYPA plans
        return filteredPlans.filter(plan =>
          plan.period === 1
          && plan.periodUnit === "year"
          && plan.id
          && plan.id.includes("5_years_paid_annually"),
        )
      }
      else if (this.is3YearRegular) {
        // 3-year regular users: Show other 3-year plans (no MYPA)
        return filteredPlans.filter(plan =>
          plan.period === 3
          && plan.periodUnit === "year",
        )
      }
      else if (this.is5YearRegular) {
        // 5-year regular users: Only show other 5-year plans (no MYPA)
        return filteredPlans.filter(plan =>
          plan.period === 5
          && plan.periodUnit === "year",
        )
      }
      else if (this.isMonthly || this.is1Year) {
        // Monthly users (annual tab) OR 1-year users: Show 1-year annual plans
        return filteredPlans.filter(plan =>
          plan.period === 1
          && plan.periodUnit === "year"
          && !plan.id?.includes("paid_annually"),
        )
      }
      else {
        // Fallback: show regular 1-year annual plans
        return filteredPlans.filter(plan =>
          plan.period === 1
          && plan.periodUnit === "year"
          && !plan.id?.includes("paid_annually"),
        )
      }
    },

    getFilteredMultiAnnualPlans() {
      // 1. Scoped logic for Checkout Wizard (No multi-annual tabs needed for new Salesforce Catalog currently)
      if (this.useCatalog) {
        return []
      }

      // 2. Preserved legacy logic for everywhere else
      let filteredPlans = this.normalizedPlans
        .filter(({ trial }) => !trial)
        .filter(({ source }) => source !== "salesforce")
        .filter(({ currencyCode }) => {
          if (this.hasPlan) {
            return currencyCode === this.currentPricingPlan.currencyCode
          }
          return currencyCode === this.visitor.currencyCode
        })

      // New subscriptions (no current plan): No multi-annual options
      if (!this.hasPlan) {
        return []
      }

      // No multi-annual plans when monthly tab is selected
      if (this.periodUnit === "month") {
        return []
      }

      if (this.isTrial || this.is3YearMYPA || this.is5YearMYPA || this.is5YearRegular) {
        // MYPA users, 5-year regular users, and trial users get no multi-annual plans (simple buttons)
        return []
      }
      else if (this.is3YearRegular) {
        // 3-year regular users: Show 5-year regular plans as multi-annual options
        return filteredPlans.filter(plan =>
          plan.period === 5
          && plan.periodUnit === "year",
        )
      }
      else if (this.isMonthly || this.is1Year) {
        // Monthly/1-year users: Show 3-year MYPA as multi-annual options
        return filteredPlans.filter(plan =>
          plan.period === 1
          && plan.periodUnit === "year"
          && plan.id
          && plan.id.includes("3_years_paid_annually"),
        )
      }
      else {
        // Fallback: show all multi-annual options
        return filteredPlans.filter(({ period, periodUnit, id }) =>
          (period > 1 && periodUnit === "year")
          || (period === 1 && periodUnit === "year" && id && id.includes("paid_annually")),
        )
      }
    },
  },
  render() {
    return this.$slots.default({
      baseMonthAnnualPlans: this.baseMonthAnnualPlans,
      currentPricingPlan: this.currentPricingPlan,
      isFetching: this.isFetching,
      trialPlan: this.trialPlan,
      multiAnnualPlans: this.multiAnnualPlans,
      canHaveMonthlyAndAnnualPlans: this.canHaveMonthlyAndAnnualPlans,
    })
  },
}
</script>
